import React, { useCallback, useMemo } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { isNil } from 'lodash'
import PropTypes from 'prop-types'

import { StyledAiHighlightedText } from '../../../common/components/styled'
import { AI_HIGHLIGHT_STYLES } from '../../../common/constants'

import {
  DISH_FIELDS_CONFIG,
  DISH_ML_INGREDIENT_TYPE,
  DISHES_AVAILABLE_HOTKEYS,
} from '../../constants'
import { useUpdateDishIngredientDiscrepancyAcceptedMutation } from '../../hooks'
import {
  StyledIngredientDiscrepancyCheckbox,
  StyledIngredientDiscrepancyLoader,
} from '../styled'

const DishIngredientsDrawerIngredient = ({
  id,
  name,
  fieldName,
  dishId,
  highlight,
  isDiscrepancyAccepted,
  isSuggestedUsingMl,
  isSuggestedUsingParser,
  isCheckboxVisible,
  isCheckboxDisabled,
  isSelected,
}) => {
  const [updateDiscrepancyAccepted, { isLoading }] =
    useUpdateDishIngredientDiscrepancyAcceptedMutation(dishId, id)

  const ingredientType = useMemo(
    () =>
      fieldName === DISH_FIELDS_CONFIG.MAIN_INGREDIENTS.value
        ? DISH_ML_INGREDIENT_TYPE.MAIN_INGREDIENT
        : DISH_ML_INGREDIENT_TYPE.CHOICE_INGREDIENT,
    [fieldName],
  )

  const handleIngredientDiscrepancyClick = useCallback(
    event => updateDiscrepancyAccepted(ingredientType, event.target.checked),
    [ingredientType, updateDiscrepancyAccepted],
  )

  useHotkeys(
    DISHES_AVAILABLE_HOTKEYS.SPACE.hotkey,
    e => {
      e.preventDefault()
      updateDiscrepancyAccepted(ingredientType, !isDiscrepancyAccepted)
    },
    {
      enabled:
        isSelected &&
        isSuggestedUsingMl !== isSuggestedUsingParser &&
        !isCheckboxDisabled,
    },
    [
      isSelected,
      isDiscrepancyAccepted,
      isSuggestedUsingMl,
      isSuggestedUsingParser,
      isCheckboxDisabled,
      ingredientType,
      updateDiscrepancyAccepted,
    ],
  )

  const isIngredientsDiscrepancyCheckboxVisible = useMemo(
    () => isCheckboxVisible && isSuggestedUsingMl !== isSuggestedUsingParser,
    [isCheckboxVisible, isSuggestedUsingMl, isSuggestedUsingParser],
  )

  if (isNil(name)) {
    return null
  }

  return (
    <>
      {isIngredientsDiscrepancyCheckboxVisible && !isLoading && (
        <StyledIngredientDiscrepancyCheckbox
          disabled={isCheckboxDisabled}
          checked={isDiscrepancyAccepted}
          onChange={handleIngredientDiscrepancyClick}
        />
      )}
      {isIngredientsDiscrepancyCheckboxVisible && isLoading && (
        <StyledIngredientDiscrepancyLoader size="small" />
      )}
      {!isNil(highlight) ? (
        <StyledAiHighlightedText
          highlights={[
            highlight,
            ...((isSuggestedUsingMl || isSuggestedUsingParser) &&
            !isDiscrepancyAccepted
              ? [AI_HIGHLIGHT_STYLES.discrepancyNotAccepted.value]
              : []),
          ]}
        >
          {name}
        </StyledAiHighlightedText>
      ) : (
        name
      )}
    </>
  )
}

DishIngredientsDrawerIngredient.propTypes = {
  id: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  fieldName: PropTypes.string.isRequired,
  dishId: PropTypes.number.isRequired,
  highlight: PropTypes.string.isRequired,
  isCheckboxVisible: PropTypes.bool,
  isDiscrepancyAccepted: PropTypes.bool,
  isSuggestedUsingMl: PropTypes.bool,
  isSuggestedUsingParser: PropTypes.bool,
  isSelected: PropTypes.bool,
  isCheckboxDisabled: PropTypes.bool,
}

DishIngredientsDrawerIngredient.defaultProps = {
  isDiscrepancyAccepted: false,
  isSuggestedUsingMl: false,
  isSuggestedUsingParser: false,
  isCheckboxVisible: false,
  isSelected: false,
  isCheckboxDisabled: false,
}

export default DishIngredientsDrawerIngredient
